import { useNavigate } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Eye, Pencil, Trash2, MoreHorizontal } from 'lucide-react'
import type { ClienteListItem } from '../types'

interface ClientesTableProps {
  clientes: ClienteListItem[]
  onDelete: (id: number) => void
}

function estadoBadge(estado: string) {
  const config: Record<string, { label: string; className: string }> = {
    activo: { label: 'Activo', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    inactivo: { label: 'Inactivo', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    prospecto: { label: 'Por validar', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  }
  const c = config[estado] || { label: estado, className: '' }
  return <Badge variant="outline" className={c.className}>{c.label}</Badge>
}

export function ClientesTable({ clientes, onDelete }: ClientesTableProps) {
  const navigate = useNavigate()

  return (
    <Table>
      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
        <TableRow>
          <TableHead>Nombre</TableHead>
          <TableHead>Laboratorio</TableHead>
          <TableHead>Cargo</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Teléfono</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {clientes.map((cliente) => (
          <TableRow key={cliente.id} className="hover:bg-gray-50 dark:hover:bg-[hsl(220,15%,16%)]">
            <TableCell>
              <div>
                <p className="font-medium">{cliente.nombre_contacto}</p>
                <p className="text-xs text-muted-foreground">{cliente.pais_nombre}</p>
              </div>
            </TableCell>
            <TableCell>{cliente.laboratorio_nombre}</TableCell>
            <TableCell>{cliente.cargo_nombre}</TableCell>
            <TableCell className="text-sm">{cliente.email}</TableCell>
            <TableCell className="text-sm">{cliente.telefono_celular}</TableCell>
            <TableCell>{estadoBadge(cliente.estado)}</TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(`/bd-clientes/${cliente.id}`)}
                  title="Ver detalle"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate(`/bd-clientes/${cliente.id}/editar`)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => onDelete(cliente.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))}
        {clientes.length === 0 && (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
              No se encontraron clientes
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
